import { diaryByIdQueryOptions } from "@/features/diaries/api";
import { resolveTrackerDueEntry } from "@/features/diaries/utils/findEarliestDueEntry";
import { extractTrackerById } from "@/features/diaries/services/extractTrackerById";
import { isTrackerBlockedBySequence } from "@/features/diaries/utils/sequentialFormHelpers";
import { redirectIfConsentRequired } from "@/features/consent/utils/redirectIfConsentRequired";
import PatientsTrackerFormPage from "@/features/pages/components/PatientsTrackerFormPage";
import { formatTrackerLateralityBanner } from "@/features/pages/utils/trackerLateralityBanner";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { usePatientsCopy } from "@/features/i18n/patientsCopy";
import type { FormViewT } from "@patient-watch/types";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";
import { formBreadcrumbLabel } from "@/features/navigation/services/entityDisplayLabel";
import { useNav } from "@/utils/nav";

const searchSchema = z.object({
    /** Editing one page of a finished entry: save returns to `returnTo`. */
    edit: z.boolean().optional(),
    returnTo: z.string().optional(),
});

const Page = () => {
    const copy = usePatientsCopy();
    const { tracker, patientName } = Route.useLoaderData();
    const { pageIndex, diaryId, trackerId, entryId } = Route.useParams();

    const { locale } = useAppLocale();

    const lateralityBanner = formatTrackerLateralityBanner(tracker?.side, locale);

    useNav([
        { label: copy.patients, route: { to: "/patients" } },
        {
            route: { to: "/patients/$diaryId", params: { diaryId } },
            label: patientName || copy.patientFallback,
        },
        {
            route: {
                to: "/patients/$diaryId/tracker/$trackerId/entry/$entryId/page/$pageIndex",
                params: { diaryId, trackerId, entryId, pageIndex },
            },
            label: formBreadcrumbLabel(tracker?.form, locale, copy.trackerEntry),
        },
    ]);

    return (
        <div className="px-2 py-4">
            <PatientsTrackerFormPage
                pageIndex={Number(pageIndex)}
                form={tracker?.form as FormViewT}
                route={Route}
                nextEntry={tracker ? resolveTrackerDueEntry(tracker) : null}
                diaryId={diaryId}
                lateralityBanner={lateralityBanner}
            />
        </div>
    );
};

export const Route = createFileRoute("/_app/patients/$diaryId/tracker/$trackerId/entry/$entryId/page/$pageIndex/")({
    validateSearch: searchSchema,
    component: Page,
    loader: async ({ context, params }) => {
        const diary = await context?.queryClient.ensureQueryData(diaryByIdQueryOptions(params.diaryId));
        if (!diary) {
            throw new Error("Diary not found");
        }
        const tracker = extractTrackerById(diary, params.trackerId);
        const isPatientViewer = context.auth?.id === diary.patient_id || context.auth?.roles?.isPatientOnly === true;

        redirectIfConsentRequired(diary, context.auth?.id, isPatientViewer);

        if (isTrackerBlockedBySequence(tracker)) {
            throw redirect({
                to: "/patients/$diaryId",
                params: { diaryId: params.diaryId },
            });
        }

        return {
            patientId: diary.patient_id ?? "",
            patientName: diary.patient_info?.patient_name ?? "Patient",
            tracker: tracker,
        };
    },
});
